import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive } from "@/pages/Senders";
import { Sparkle, Trash } from "@phosphor-icons/react";
import { useAiModels, ModelOptions, defaultModelKey } from "@/lib/aiModels";

const PLACEHOLDERS = ["firstName","lastName","fullName","email","phone","loanAmount","address","city","state","zip","ctaLink","companyName","senderFirstName","senderFullName","senderEmail"];

export default function Content() {
  const [tab, setTab] = useState("manual");
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("Untitled template");
  const [subject, setSubject] = useState("Quick question about [companyName]");
  const [textBody, setTextBody] = useState("Hi [firstName],\n\nI noticed [companyName] and wanted to reach out.\n\nWould you have 10 minutes this week?\n\nThanks again\n[senderFullName]");
  const [htmlBody, setHtmlBody] = useState("");
  const [variations, setVariations] = useState([]);
  const [studio, setStudio] = useState({
    assembly_combination: "",
    intent: "",
    support_tone: "",
    support_context: "request",
    fast_tone: "",
    soft_tone: "",
    cta_action: "",
    subject_tone: "",
    greeting: "",
    sign_off: "",
    language: "English",
    html: false,
    instructions: "",
  });
  const [advOpen, setAdvOpen] = useState(false);
  const [aiOutput, setAiOutput] = useState(null);
  const [previewOut, setPreviewOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [spintaxErr, setSpintaxErr] = useState("");
  const { models: aiModels } = useAiModels();
  const [aiModel, setAiModel] = useState("");
  const [aiThinking, setAiThinking] = useState("");
  useEffect(() => { if (!aiModel && aiModels.length) setAiModel(defaultModelKey(aiModels)); }, [aiModels]); // eslint-disable-line

  const loadTemplates = async () => {
    const r = await api.get("/content/templates");
    setTemplates(r.data.templates);
  };
  useEffect(() => { loadTemplates(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/content/templates", {
        name, content_type: tab, subject, text_body: textBody, html_body: htmlBody,
        ai_settings: tab === "ai" ? studio : {},
      });
      toast.success("Template saved");
      loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const doPreview = async () => {
    const r = await api.post("/content/preview", { subject, text_body: textBody, html_body: htmlBody, sample_values: {
      firstName: "Sam", lastName: "Lee", fullName: "Sam Lee", companyName: "Acme", city: "Austin",
      ctaLink: "https://example.com/apply", senderFullName: "Alex Kim", senderEmail: "alex@yourco.com",
    }});
    setPreviewOut(r.data);
  };

  const spintaxValidate = async () => {
    const r = await api.post("/content/spintax/validate", { text: subject + "\n" + textBody });
    setSpintaxErr(r.data.valid ? "" : `${r.data.error}`);
    if (r.data.valid) toast.success("Spintax valid");
  };

  const spintaxPreview = async (count) => {
    try {
      const r = await api.post("/content/spintax/preview", { text: textBody, count });
      setVariations(r.data.variations);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid spintax");
    }
  };

  const aiGenerate = async () => {
    setBusy(true);
    try {
      const count = studio.count || 1;
      const r = await api.post("/content/ai/generate", {
        model: aiModel,
        controls: { language: studio.language, instructions: studio.instructions, html: studio.html, thinking: aiThinking },
        count,
      });
      if (r.data.variations) {
        // multi-variation response
        setAiOutput({ variations: r.data.variations });
        // Load the FIRST successful variation into the editor
        const first = r.data.variations.find((v) => v.text_body);
        if (first) {
          setSubject(first.subject || subject);
          setTextBody(first.text_body);
        }
      } else {
        setAiOutput(r.data);
        setSubject(r.data.subject || subject);
        setTextBody(r.data.text_body);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed. Configure DeepSeek API key in Settings.");
    } finally { setBusy(false); }
  };

  const insertPlaceholder = (p) => {
    setTextBody((t) => t + `[${p}]`);
  };

  const delTpl = async (id) => {
    await api.delete(`/content/templates/${id}`);
    toast.success("Deleted");
    loadTemplates();
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="micro mb-1">Content</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Templates</h1>
        </div>
        <BtnPrimary onClick={save} testid="content-save" disabled={busy}>Save template</BtnPrimary>
      </div>

      <div className="flex border border-[#2A2A2A] w-fit mb-6">
        {[
          ["manual", "Manual"],
          ["spintax", "Spintax"],
          ["ai", "AI Studio"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-5 py-2 text-xs uppercase tracking-wider font-medium transition-colors ${tab === k ? "bg-[#E1FF00] text-black" : "text-[#A1A1AA] hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-4">
          <label className="block">
            <span className="micro block mb-1.5">Template name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="content-name"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
          <label className="block">
            <span className="micro block mb-1.5">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="content-subject"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
          <label className="block">
            <span className="micro block mb-1.5">Text body</span>
            <textarea value={textBody} onChange={(e) => setTextBody(e.target.value)} rows={16} data-testid="content-text-body"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
          </label>
          <label className="block">
            <span className="micro block mb-1.5">HTML body (optional)</span>
            <textarea value={htmlBody} onChange={(e) => setHtmlBody(e.target.value)} rows={5} data-testid="content-html-body"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
          </label>

          <div className="flex gap-2 flex-wrap">
            <BtnSecondary onClick={doPreview} testid="content-preview-btn">Preview</BtnSecondary>
            {tab === "spintax" && (
              <>
                <BtnSecondary onClick={spintaxValidate} testid="spintax-validate">Validate spintax</BtnSecondary>
                <BtnSecondary onClick={() => spintaxPreview(5)} testid="spintax-5">5 variations</BtnSecondary>
                <BtnSecondary onClick={() => spintaxPreview(10)} testid="spintax-10">10 variations</BtnSecondary>
              </>
            )}
          </div>
          {spintaxErr && <div className="text-xs font-mono text-[#FF3B30]">{spintaxErr}</div>}
        </div>

        <div className="space-y-4">
          <div className="border border-[#2A2A2A] p-4">
            <div className="micro mb-2">Placeholders</div>
            <div className="flex flex-wrap gap-1">
              {PLACEHOLDERS.map((p) => (
                <button key={p} onClick={() => insertPlaceholder(p)} data-testid={`insert-${p}`}
                  className="font-mono text-[11px] px-1.5 py-0.5 border border-[#2A2A2A] hover:border-[#E1FF00] hover:text-[#E1FF00] text-[#A1A1AA]">
                  [{p}]
                </button>
              ))}
            </div>
            <div className="micro mt-3 mb-1 text-[#71717A]">Studio auto-inserts</div>
            <div className="flex flex-wrap gap-1">
              {["loanAmount", "firstName", "phone", "ctaLink", "senderFullName"].map((p) => (
                <span key={p} className="font-mono text-[11px] px-1.5 py-0.5 border border-[#00E5FF]/30 text-[#00E5FF]">[{p}]</span>
              ))}
            </div>
            <div className="text-[10px] text-[#71717A] mt-1 leading-snug">
              AI Studio emits these standard tokens verbatim; the sending engine substitutes real values per recipient.
            </div>
          </div>

          {tab === "ai" && (
            <div className="border border-[#2A2A2A] p-4 space-y-3" data-testid="ai-studio-panel">
              <div className="micro">AI Content Studio</div>
              <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
                Each generation randomly rotates through the <b className="text-[#E1FF00]">full spec</b>:
                16 intent families, 15 support tones, 18 fast tones, 16 soft tones, 22 CTAs, 18 subject tones,
                12 greetings, 5 sign-offs. Every email is a unique mix — no repeats.
              </p>

              <label className="block">
                <span className="micro block mb-1">Language</span>
                <input value={studio.language}
                  onChange={(e) => setStudio({ ...studio, language: e.target.value })}
                  data-testid="ai-language"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-1.5 text-xs text-white" />
              </label>

              <label className="block">
                <span className="micro block mb-1">Custom instructions (optional)</span>
                <textarea rows={3} value={studio.instructions}
                  onChange={(e) => setStudio({ ...studio, instructions: e.target.value })}
                  placeholder="Extra guidance for the model. Leave blank to use the spec as-is."
                  data-testid="ai-instructions"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono" />
              </label>

              <label className="block">
                <span className="micro block mb-1">AI model</span>
                <select value={aiModel} onChange={(e) => setAiModel(e.target.value)}
                  data-testid="ai-model-select"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-1.5 text-xs text-white font-mono">
                  <ModelOptions models={aiModels} />
                </select>
              </label>

              <label className="block">
                <span className="micro block mb-1">Thinking mode</span>
                <select value={aiThinking} onChange={(e) => setAiThinking(e.target.value)}
                  data-testid="ai-thinking-select"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-1.5 text-xs text-white font-mono">
                  <option value="">Default (off for bulk)</option>
                  <option value="disabled">Disabled (fast)</option>
                  <option value="enabled">Enabled (reasoning · slower)</option>
                </select>
              </label>

              <label className="block">
                <span className="micro block mb-1">How many variations to generate</span>
                <select value={studio.count || 1}
                  onChange={(e) => setStudio({ ...studio, count: Number(e.target.value) })}
                  data-testid="ai-count"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-1.5 text-xs text-white">
                  {[1, 3, 5, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>

              <BtnPrimary onClick={aiGenerate} testid="ai-generate" disabled={busy}>
                <Sparkle size={12} weight="fill" /> {busy ? "GENERATING..." : "Generate unique email(s)"}
              </BtnPrimary>
            </div>
          )}

          <div className="border border-[#2A2A2A] p-4">
            <div className="micro mb-2">Saved templates</div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {templates.length === 0 && <div className="text-xs text-[#71717A] font-mono">[ none ]</div>}
              {templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-xs py-1 border-b border-[#1A1A1A]">
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="font-mono text-[10px] text-[#71717A] uppercase tracking-wider">{t.content_type}</div>
                  </div>
                  <button onClick={() => delTpl(t.id)} data-testid={`tpl-del-${t.id}`}
                    className="text-[#71717A] hover:text-[#FF3B30]">
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {(previewOut || variations.length > 0 || aiOutput) && (
        <div className="mt-8 border border-[#2A2A2A] p-6" data-testid="preview-output">
          <div className="micro mb-2">Output</div>
          {previewOut && (
            <div className="space-y-3">
              <div>
                <div className="micro text-[#71717A] mb-1">Subject</div>
                <div className="font-mono text-sm">{previewOut.subject}</div>
              </div>
              <div>
                <div className="micro text-[#71717A] mb-1">Body</div>
                <pre className="font-mono text-xs text-[#E4E4E7] whitespace-pre-wrap">{previewOut.text_body}</pre>
              </div>
              {previewOut.missing_placeholders?.length > 0 && (
                <div className="text-xs font-mono text-[#FF9500]">Missing: {previewOut.missing_placeholders.join(", ")}</div>
              )}
            </div>
          )}
          {variations.length > 0 && (
            <div className="space-y-4">
              {variations.map((v, i) => (
                <div key={i}>
                  <div className="micro text-[#71717A] mb-1">Variation {i + 1}</div>
                  <pre className="font-mono text-xs text-[#E4E4E7] whitespace-pre-wrap border-l-2 border-[#E1FF00] pl-3">{v}</pre>
                </div>
              ))}
            </div>
          )}
          {aiOutput && !aiOutput.variations && (
            <div>
              <div className="micro text-[#71717A] mb-1">
                AI generated · studio
                {aiOutput.spec_used && (
                  <span className="ml-2 text-[10px] text-[#00E5FF] font-mono uppercase">
                    [{aiOutput.spec_used.intent}/{aiOutput.spec_used.cta_action}]
                  </span>
                )}
              </div>
              <pre className="font-mono text-xs text-[#E4E4E7] whitespace-pre-wrap">{aiOutput.raw}</pre>
            </div>
          )}
          {aiOutput?.variations && (
            <div className="space-y-6">
              {aiOutput.variations.map((v, i) => (
                <div key={i} className="border-l-2 border-[#E1FF00] pl-4">
                  <div className="micro text-[#71717A] mb-1">
                    Variation {i + 1}
                    {v.spec_used && (
                      <span className="ml-2 text-[10px] text-[#00E5FF] font-mono uppercase">
                        [{v.spec_used.intent}/{v.spec_used.cta_action}/{v.spec_used.intent_tone}]
                      </span>
                    )}
                  </div>
                  {v.error ? (
                    <div className="text-xs font-mono text-[#FF3B30]">{v.error}</div>
                  ) : (
                    <>
                      <div className="text-xs mb-1"><b>Subject:</b> {v.subject}</div>
                      <pre className="font-mono text-xs text-[#E4E4E7] whitespace-pre-wrap">{v.text_body}</pre>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
